import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Mail, Loader2, Sparkles, Copy } from 'lucide-react';
import { toast } from 'sonner';

const EMAIL_TYPES = [
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'promo', label: 'Promotion' },
  { value: 'drip', label: 'Drip sequence step' },
  { value: 'announcement', label: 'Announcement' },
];

export const EmailGeneratorPage = () => {
  const { api } = useAuth();
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('Professional');
  const [brand, setBrand] = useState('');
  const [emailType, setEmailType] = useState('newsletter');
  const [cta, setCta] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!topic.trim() || !audience.trim()) {
      toast.error('Topic and Audience are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/generate/email', {
        topic: topic.trim(),
        audience: audience.trim(),
        tone,
        brand_personality: brand.trim() || undefined,
        email_type: emailType,
        cta: cta.trim() || undefined,
      });
      setResult(res.data);
      toast.success('Email generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const cj = result?.content_json || {};

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-6" data-testid="email-generator-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-indigo-600" />
          AI Email Marketing
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Subject lines, preheader, HTML + plain body, and CTA — optimized for deliverability.
        </p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="border-zinc-200 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Email details</CardTitle>
            <CardDescription>3 subject variants + HTML body generated.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="em-topic">Topic *</Label>
              <Input id="em-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="New product launch" data-testid="email-topic-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em-audience">Audience *</Label>
              <Input id="em-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Existing customers, last 30 days" data-testid="email-audience-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email type</Label>
              <Select value={emailType} onValueChange={setEmailType}>
                <SelectTrigger data-testid="email-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMAIL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em-tone">Tone</Label>
              <Input id="em-tone" value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Professional" data-testid="email-tone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em-brand">Brand personality</Label>
              <Input id="em-brand" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="warm, helpful" data-testid="email-brand-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em-cta">Main CTA</Label>
              <Input id="em-cta" value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Shop the new collection" data-testid="email-cta-input" />
            </div>
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleGenerate}
              disabled={loading}
              data-testid="email-generate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate email
            </Button>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 min-h-[420px]">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                <p className="text-sm text-zinc-500">Writing your email...</p>
              </div>
            ) : !result ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Mail className="w-12 h-12 text-zinc-200 mb-3" />
                <p className="text-sm text-zinc-500">Your email will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="email-result">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Subject lines</p>
                    <Badge variant="outline" className="text-[10px]">{(cj.subject_lines || []).length} variants</Badge>
                  </div>
                  <div className="space-y-1.5">
                    {(cj.subject_lines || []).map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 p-2 rounded-md border border-zinc-200 hover:bg-zinc-50">
                        <div className="flex items-center gap-2 min-w-0">
                          {i === 0 && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">Primary</Badge>}
                          <span className="text-sm font-medium text-zinc-900 truncate">{s}</span>
                          <span className="text-[10px] text-zinc-400 shrink-0">{s.length}ch</span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => copy(s, `Subject ${i + 1}`)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {cj.preheader && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Preheader</p>
                    <div className="p-2 rounded-md bg-zinc-50 border border-zinc-200 text-sm italic">{cj.preheader}</div>
                  </div>
                )}

                <Tabs defaultValue="preview">
                  <div className="flex items-center justify-between">
                    <TabsList>
                      <TabsTrigger value="preview" data-testid="email-tab-preview">Preview</TabsTrigger>
                      <TabsTrigger value="html" data-testid="email-tab-html">HTML</TabsTrigger>
                      <TabsTrigger value="plain" data-testid="email-tab-plain">Plain text</TabsTrigger>
                    </TabsList>
                    <Button size="sm" variant="outline" onClick={() => copy(cj.body_html || cj.body_plaintext || '', 'Body')}>
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                      Copy body
                    </Button>
                  </div>
                  <TabsContent value="preview">
                    <ScrollArea className="h-[360px] rounded-md border border-zinc-200 p-4 bg-white">
                      <div className="prose prose-zinc max-w-none text-sm" dangerouslySetInnerHTML={{ __html: cj.body_html || '' }} />
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="html">
                    <ScrollArea className="h-[360px] rounded-md border border-zinc-200 p-4 bg-zinc-950">
                      <pre className="text-[11px] text-emerald-300 whitespace-pre-wrap">{cj.body_html}</pre>
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="plain">
                    <ScrollArea className="h-[360px] rounded-md border border-zinc-200 p-4 bg-zinc-50">
                      <pre className="text-sm text-zinc-800 whitespace-pre-wrap font-sans">{cj.body_plaintext}</pre>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>

                {cj.cta_text && (
                  <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 mb-1">Call-to-action</p>
                    <p className="text-sm text-indigo-900 font-medium">{cj.cta_text}</p>
                    {cj.cta_url_placeholder && <p className="text-[10px] text-indigo-600 mt-0.5">→ {cj.cta_url_placeholder}</p>}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
